import { useEffect, useState } from 'react';

import { Input } from '@flintmint/web-shared/components/input';
import { Switch } from '@flintmint/web-shared/components/switch';
import { Button } from '@flintmint/web-shared/components/button';
import { Popover } from '@flintmint/web-shared/components/popover';

import { itemTypes } from '@/constants/item-types';
import { MAX_DOCUMENT_NAME_LENGTH } from '@/extensions/content-size-limit';

import styles from './save-as-confirmation.module.scss';

export const SaveAsConfirmation = (props: SaveAsConfirmationProps) => {
  const { type, onSave, isAttachedToDoc, documentName, noteVariant, disabled } = props;

  const [open, setOpen] = useState(false);
  const [name, setName] = useState(documentName);
  const [replaceWithNew, setReplaceWithNew] = useState(false);

  useEffect(() => {
    if (documentName !== name) {
      setName(documentName);
    }
  }, [documentName]);

  const content = {
    userSources: {
      description: `If enabled, the original Cortex item reference in this ${itemTypes[
        noteVariant!
      ]?.name?.toLowerCase()} will be replaced with this new item.`,
      switchLabel: `Replace the Cortex Item in the ${itemTypes[noteVariant!]?.name}`,
    },
    blocks: {
      switchLabel: `Replace the Block in the ${itemTypes[noteVariant!]?.name}`,
      description: `When enabled, the newly created block will replace the current one in the ${itemTypes[noteVariant!]?.name}.`,
    },
  };

  return (
    <Popover
      arrow
      open={open}
      offset={[0, 10]}
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false);
        setName(documentName);
      }}
      content={
        <div className={styles.saveAsConfirmation}>
          <label>
            <span className={styles.inputLabel}>New Name</span>
            <Input
              size="big"
              value={name}
              maxLength={MAX_DOCUMENT_NAME_LENGTH}
              appearance="filled"
              data-testid="save-as-new-input"
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          {!!isAttachedToDoc && !!noteVariant && (
            <div>
              <Switch
                checked={replaceWithNew}
                label={content[type].switchLabel}
                data-testid="replace-with-new-switch"
                onChange={() => setReplaceWithNew((val) => !val)}
              />
              <span className={styles.description}>{content[type].description}</span>
            </div>
          )}
          <Button
            fullWidth
            color="success"
            disabled={!name || disabled}
            data-testid="save-as-new-btn"
            onClick={() => {
              onSave(name, replaceWithNew);
              setOpen(false);
            }}
          >
            {replaceWithNew ? 'Save & Replace' : 'Save'}
          </Button>
        </div>
      }
      placement="bottom"
    >
      <button type="button" disabled={disabled}>
        as New
      </button>
    </Popover>
  );
};

export interface SaveAsConfirmationProps {
  documentName: string;
  noteVariant?: string;
  isAttachedToDoc?: boolean;
  type: 'userSources' | 'blocks';
  onSave: (name: string, replace: boolean) => void;
  disabled?: boolean;
}
